if (!customElements.get('product-info')) {
  customElements.define(
    'product-info',
    class ProductInfo extends HTMLElement {
      quantityInput = undefined;
      quantityForm = undefined;
      onVariantChangeUnsubscriber = undefined;
      cartUpdateUnsubscriber = undefined;
      subscriptionHeadingVariantUnsubscriber = undefined;
      subscriptionHeadingObservers = [];
      subscriptionHeadingAppstleListeners = [];
      abortController = undefined;
      pendingRequestUrl = null;
      preProcessHtmlCallbacks = [];
      postProcessHtmlCallbacks = [];

      constructor() {
        super();

        this.quantityInput = this.querySelector('.quantity__input');
      }

      connectedCallback() {
        this.initializeProductSwapUtility();

        this.onVariantChangeUnsubscriber = subscribe(
          PUB_SUB_EVENTS.optionValueSelectionChange,
          this.handleOptionValueChange.bind(this)
        );

        this.initQuantityHandlers();
        this.initSubscriptionPurchaseHeadingVisibility();
        this.initProductFormAvailability();
        this.dispatchEvent(new CustomEvent('product-info:loaded', { bubbles: true }));
      }

      initProductFormAvailability() {
        const sync = () => this.syncSubmitButtonFromServer(this);
        const runWhenReady = () => {
          const productForm = this.productForm;
          if (typeof productForm?.toggleSubmitButton === 'function') {
            sync();
            return;
          }
          if (customElements.get('product-form')) {
            // 定義済みだがインスタンス未アップグレードのとき、次フレームで再試行
            requestAnimationFrame(sync);
            return;
          }
          customElements.whenDefined('product-form').then(() => requestAnimationFrame(sync)).catch(() => {});
        };
        queueMicrotask(runWhenReady);
        [100, 500, 1000].forEach((delay) => setTimeout(runWhenReady, delay));
      }

      /**
       * 在庫判定は Liquid 側（buy-buttons）に任せ、サーバー描画済みの submit ボタン状態を信頼する。
       * クライアントの variant JSON には inventory_quantity が欠けることがあり、誤って売り切れになる。
       */
      syncSubmitButtonFromServer(root = this) {
        const submitButton =
          root.getElementById?.(`ProductSubmitButton-${this.sectionId}`) ??
          root.querySelector?.(`#ProductSubmitButton-${this.sectionId}`);
        const productForm = this.productForm;

        if (!submitButton || typeof productForm?.toggleSubmitButton !== 'function') return;

        const soldOut = submitButton.hasAttribute('disabled');
        const label = submitButton.querySelector('span')?.textContent?.trim();

        productForm.toggleSubmitButton(
          soldOut,
          soldOut ? label || window.variantStrings.soldOut : undefined
        );
      }

      addPreProcessCallback(callback) {
        this.preProcessHtmlCallbacks.push(callback);
      }

      initQuantityHandlers() {
        if (!this.quantityInput) return;

        this.quantityForm = this.querySelector('.product-form__quantity');
        if (!this.quantityForm) return;

        this.setQuantityBoundries();
        if (!this.dataset.originalSection) {
          this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, this.fetchQuantityRules.bind(this));
        }
      }

      disconnectedCallback() {
        this.onVariantChangeUnsubscriber();
        this.cartUpdateUnsubscriber?.();
        this.subscriptionHeadingVariantUnsubscriber?.();
        clearTimeout(this._subscriptionHeadingSyncScheduled);
        this.teardownSubscriptionPurchaseHeadingVisibility();
      }

      initializeProductSwapUtility() {
        this.preProcessHtmlCallbacks.push((html) =>
          html.querySelectorAll('.scroll-trigger').forEach((element) => element.classList.add('scroll-trigger--cancel'))
        );
        this.postProcessHtmlCallbacks.push((newNode) => {
          window?.Shopify?.PaymentButton?.init();
          window?.ProductModel?.loadShopifyXR();
          if (newNode?.tagName === 'PRODUCT-INFO') {
            newNode.syncPurchaseOptionsHeadingWithSubscription?.();
          }
        });
      }

      initSubscriptionPurchaseHeadingVisibility() {
        this.subscriptionHeadingVariantUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
          if (event.data?.sectionId !== this.sectionId) return;
          this.scheduleSyncPurchaseOptionsHeadingWithSubscription();
        });

        const onAppstleWidgetChange = () => this.scheduleSyncPurchaseOptionsHeadingWithSubscription();
        ['AppstleSubscription:SubscriptionWidget:widgetInitialised', 'AppstleSubscription:SubscriptionWidget:SubscriptionWidgetUpdated'].forEach(
          (eventName) => {
            document.addEventListener(eventName, onAppstleWidgetChange);
            this.subscriptionHeadingAppstleListeners.push({ eventName, listener: onAppstleWidgetChange });
          }
        );

        queueMicrotask(() => this.syncPurchaseOptionsHeadingWithSubscription());
        [100, 500, 1000, 2000, 3500].forEach((delay) =>
          setTimeout(() => this.syncPurchaseOptionsHeadingWithSubscription(), delay)
        );
      }

      scheduleSyncPurchaseOptionsHeadingWithSubscription() {
        clearTimeout(this._subscriptionHeadingSyncScheduled);
        this._subscriptionHeadingSyncScheduled = setTimeout(
          () => this.syncPurchaseOptionsHeadingWithSubscription(),
          50
        );
      }

      teardownSubscriptionHeadingObservers() {
        (this.subscriptionHeadingObservers || []).forEach((observer) => observer.disconnect());
        this.subscriptionHeadingObservers = [];
        clearTimeout(this._subscriptionHeadingObsDebounce);
      }

      teardownSubscriptionPurchaseHeadingVisibility() {
        this.teardownSubscriptionHeadingObservers();
        (this.subscriptionHeadingAppstleListeners || []).forEach(({ eventName, listener }) => {
          document.removeEventListener(eventName, listener);
        });
        this.subscriptionHeadingAppstleListeners = [];
      }

      isAppstleSubscriptionAppBlock(element) {
        if (!element?.matches?.('.shopify-app-block')) return false;
        const handle = (element.getAttribute('data-block-handle') || '').toLowerCase();
        if (handle.includes('appstle')) return true;
        return !!element.querySelector('[class*="appstle"], [id*="appstle"], [class*="Appstle"]');
      }

      findAppstleSubscriptionWidget(appBlock) {
        return (
          appBlock.querySelector(
            '[class*="appstle_"], [id*="appstle"], [class*="appstle-"], [class*="Appstle"]'
          ) || appBlock
        );
      }

      subscriptionWidgetHasVisibleUi(root) {
        if (!root) return false;
        const cs = window.getComputedStyle(root);
        const rect = root.getBoundingClientRect();
        const notHidden =
          cs.display !== 'none' && cs.visibility !== 'hidden' && Number(cs.opacity) > 0 && rect.height >= 6;
        if (!notHidden) return false;
        return !!root.querySelector(
          'input, button, select, textarea, label, a[href], [role="radio"], [role="tab"], [role="group"], [role="button"]'
        );
      }

      findNextAppstleSubscriptionBlock(heading) {
        let element = heading.nextElementSibling;
        while (element) {
          if (this.isAppstleSubscriptionAppBlock(element)) return element;
          if (
            element.matches(
              '.product-form__quantity, product-form, .product-form__buttons, .shopify-payment-button, variant-selects, .product-sibling-variants'
            )
          ) {
            break;
          }
          element = element.nextElementSibling;
        }
        return null;
      }

      applyPurchaseOptionsHeadingSubscriptionVisibility(heading, visible) {
        heading.classList.toggle('purchase-options-heading-block--subscription-hidden', !visible);
        heading.toggleAttribute('hidden', !visible);
        heading.setAttribute('aria-hidden', visible ? 'false' : 'true');
      }

      /**
       * Appstle Subscription は商品・バリエーションによって DOM だけ残り中身が空になることがある。
       * 直後の Appstle アプリブロックに実 UI があるときだけ「購入オプション」見出しを残す。
       */
      syncPurchaseOptionsHeadingWithSubscription() {
        if (window.Shopify?.designMode) {
          this.querySelectorAll('.purchase-options-heading-block--subscription-hidden').forEach((heading) => {
            heading.classList.remove('purchase-options-heading-block--subscription-hidden');
            heading.removeAttribute('hidden');
            heading.setAttribute('aria-hidden', 'false');
          });
          this.teardownSubscriptionPurchaseHeadingVisibility();
          return;
        }

        this.teardownSubscriptionHeadingObservers();

        this.querySelectorAll('.purchase-options-heading-block').forEach((heading) => {
          const subscriptionBlock = this.findNextAppstleSubscriptionBlock(heading);
          if (!subscriptionBlock) {
            this.applyPurchaseOptionsHeadingSubscriptionVisibility(heading, false);
            return;
          }

          const evaluate = () => {
            const widget = this.findAppstleSubscriptionWidget(subscriptionBlock);
            this.applyPurchaseOptionsHeadingSubscriptionVisibility(
              heading,
              this.subscriptionWidgetHasVisibleUi(widget)
            );
          };

          evaluate();

          const observer = new MutationObserver(() => {
            clearTimeout(this._subscriptionHeadingObsDebounce);
            this._subscriptionHeadingObsDebounce = setTimeout(evaluate, 80);
          });
          observer.observe(subscriptionBlock, {
            childList: true,
            subtree: true,
            attributes: true,
            attributeFilter: ['class', 'style', 'hidden', 'aria-hidden'],
          });
          this.subscriptionHeadingObservers.push(observer);
        });
      }

      handleOptionValueChange({ data: { event, target, selectedOptionValues } }) {
        if (!this.contains(event.target)) return;

        this.resetProductFormState();

        const productUrl = target.dataset.productUrl || this.pendingRequestUrl || this.dataset.url;
        this.pendingRequestUrl = productUrl;
        const shouldSwapProduct = this.dataset.url !== productUrl;
        const shouldFetchFullPage = this.dataset.updateUrl === 'true' && shouldSwapProduct;

        this.renderProductInfo({
          requestUrl: this.buildRequestUrlWithParams(productUrl, selectedOptionValues, shouldFetchFullPage),
          targetId: target.id,
          callback: shouldSwapProduct
            ? this.handleSwapProduct(productUrl, shouldFetchFullPage)
            : this.handleUpdateProductInfo(productUrl),
        });
      }

      resetProductFormState() {
        const productForm = this.productForm;
        productForm?.toggleSubmitButton(true);
        productForm?.handleErrorMessage();
      }

      handleSwapProduct(productUrl, updateFullPage) {
        return (html) => {
          this.productModal?.remove();

          const selector = updateFullPage ? "product-info[id^='MainProduct']" : 'product-info';
          const variant = this.getSelectedVariant(html.querySelector(selector));
          this.updateURL(productUrl, variant?.id);

          if (updateFullPage) {
            document.querySelector('head title').innerHTML = html.querySelector('head title').innerHTML;

            HTMLUpdateUtility.viewTransition(
              document.querySelector('main'),
              html.querySelector('main'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          } else {
            HTMLUpdateUtility.viewTransition(
              this,
              html.querySelector('product-info'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          }
        };
      }

      renderProductInfo({ requestUrl, targetId, callback }) {
        this.abortController?.abort();
        this.abortController = new AbortController();

        fetch(requestUrl, { signal: this.abortController.signal })
          .then((response) => response.text())
          .then((responseText) => {
            this.pendingRequestUrl = null;
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            callback(html);
          })
          .then(() => {
            // set focus to last clicked option value
            document.querySelector(`#${targetId}`)?.focus();
          })
          .catch((error) => {
            if (error.name === 'AbortError') {
              console.log('Fetch aborted by user');
            } else {
              console.error(error);
            }
          });
      }

      getSelectedVariant(productInfoNode) {
        const selectedVariant = productInfoNode.querySelector('variant-selects [data-selected-variant]')?.innerHTML;
        return !!selectedVariant ? JSON.parse(selectedVariant) : null;
      }

      buildRequestUrlWithParams(url, optionValues, shouldFetchFullPage = false) {
        const params = [];

        !shouldFetchFullPage && params.push(`section_id=${this.sectionId}`);

        if (optionValues.length) {
          params.push(`option_values=${optionValues.join(',')}`);
        }

        return `${url}?${params.join('&')}`;
      }

      updateOptionValues(html) {
        const variantSelects = html.querySelector('variant-selects');
        if (variantSelects) {
          HTMLUpdateUtility.viewTransition(this.variantSelectors, variantSelects, this.preProcessHtmlCallbacks);
        }
      }

      handleUpdateProductInfo(productUrl) {
        return (html) => {
          const variant = this.getSelectedVariant(html);

          this.pickupAvailability?.update(variant);
          this.updateOptionValues(html);
          this.updateURL(productUrl, variant?.id);
          this.updateVariantInputs(variant?.id);
          this.updateProductFormVariantJson(variant);

          if (!variant) {
            this.setUnavailable();
            return;
          }

          this.updateMedia(html, variant?.featured_media?.id);

          const updateSourceFromDestination = (id, shouldHide = (source) => false) => {
            const source = html.getElementById(`${id}-${this.sectionId}`);
            const destination = this.querySelector(`#${id}-${this.dataset.section}`);
            if (source && destination) {
              destination.innerHTML = source.innerHTML;
              destination.classList.toggle('hidden', shouldHide(source));
            }
          };

          updateSourceFromDestination('price');
          updateSourceFromDestination('Sku', ({ classList }) => classList.contains('hidden'));
          updateSourceFromDestination('Inventory', ({ innerText }) => innerText === '');
          updateSourceFromDestination('Volume');
          updateSourceFromDestination('Price-Per-Item', ({ classList }) => classList.contains('hidden'));

          this.updateQuantityRules(this.sectionId, html);
          this.querySelector(`#Quantity-Rules-${this.dataset.section}`)?.classList.remove('hidden');
          this.querySelector(`#Volume-Note-${this.dataset.section}`)?.classList.remove('hidden');

          this.syncSubmitButtonFromServer(html);
          this.updatePaymentButtons(html);

          publish(PUB_SUB_EVENTS.variantChange, {
            data: {
              sectionId: this.sectionId,
              html,
              variant,
            },
          });
        };
      }

      updateVariantInputs(variantId) {
        this.querySelectorAll(
          `#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`
        ).forEach((productForm) => {
          const input = productForm.querySelector('input[name="id"]');
          input.value = variantId ?? '';
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      }

      updateProductFormVariantJson(variant) {
        const jsonNode = this.productForm?.querySelector('[data-selected-variant]');
        if (!jsonNode) return;
        jsonNode.textContent = variant ? JSON.stringify(variant) : '';
      }

      updateURL(url, variantId) {
        this.querySelector('share-button')?.updateUrl(
          `${window.shopUrl}${url}${variantId ? `?variant=${variantId}` : ''}`
        );

        if (this.dataset.updateUrl === 'false') return;
        window.history.replaceState({}, '', `${url}${variantId ? `?variant=${variantId}` : ''}`);
      }

      setUnavailable() {
        this.productForm?.toggleSubmitButton(true, window.variantStrings.unavailable);

        const selectors = ['price', 'Inventory', 'Sku', 'Price-Per-Item', 'Volume-Note', 'Volume', 'Quantity-Rules']
          .map((id) => `#${id}-${this.dataset.section}`)
          .join(', ');
        document.querySelectorAll(selectors).forEach(({ classList }) => classList.add('hidden'));
      }

      updatePaymentButtons(html) {
        const sourceButtons = html.getElementById(`ProductFormButtons-${this.sectionId}`);
        const destinationButtons = this.querySelector(`#ProductFormButtons-${this.sectionId}`);
        if (!sourceButtons || !destinationButtons) return;

        const sourcePaymentButton = sourceButtons.querySelector('.shopify-payment-button');
        const destinationPaymentButton = destinationButtons.querySelector('.shopify-payment-button');
        if (!sourcePaymentButton || !destinationPaymentButton) return;

        destinationPaymentButton.replaceWith(sourcePaymentButton.cloneNode(true));
        window?.Shopify?.PaymentButton?.init();
        this.syncSubmitButtonFromServer(html);
      }

      updateMedia(html, variantFeaturedMediaId) {
        if (!variantFeaturedMediaId) return;

        const mediaGallerySource = this.querySelector('media-gallery ul');
        const mediaGalleryDestination = html.querySelector(`media-gallery ul`);

        const refreshSourceData = () => {
          if (this.hasAttribute('data-zoom-on-hover')) enableZoomOnHover(2);
          const mediaGallerySourceItems = Array.from(mediaGallerySource.querySelectorAll('li[data-media-id]'));
          const sourceSet = new Set(mediaGallerySourceItems.map((item) => item.dataset.mediaId));
          const sourceMap = new Map(
            mediaGallerySourceItems.map((item, index) => [item.dataset.mediaId, { item, index }])
          );
          return [mediaGallerySourceItems, sourceSet, sourceMap];
        };

        if (mediaGallerySource && mediaGalleryDestination) {
          let [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
          const mediaGalleryDestinationItems = Array.from(
            mediaGalleryDestination.querySelectorAll('li[data-media-id]')
          );
          const destinationSet = new Set(mediaGalleryDestinationItems.map(({ dataset }) => dataset.mediaId));
          let shouldRefresh = false;

          // add items from new data not present in DOM
          for (let i = mediaGalleryDestinationItems.length - 1; i >= 0; i--) {
            if (!sourceSet.has(mediaGalleryDestinationItems[i].dataset.mediaId)) {
              mediaGallerySource.prepend(mediaGalleryDestinationItems[i]);
              shouldRefresh = true;
            }
          }

          // remove items from DOM not present in new data
          for (let i = 0; i < mediaGallerySourceItems.length; i++) {
            if (!destinationSet.has(mediaGallerySourceItems[i].dataset.mediaId)) {
              mediaGallerySourceItems[i].remove();
              shouldRefresh = true;
            }
          }

          // refresh
          if (shouldRefresh) [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();

          // if media galleries don't match, sort to match new data order
          mediaGalleryDestinationItems.forEach((destinationItem, destinationIndex) => {
            const sourceData = sourceMap.get(destinationItem.dataset.mediaId);

            if (sourceData && sourceData.index !== destinationIndex) {
              mediaGallerySource.insertBefore(
                sourceData.item,
                mediaGallerySource.querySelector(`li:nth-of-type(${destinationIndex + 1})`)
              );

              // refresh source now that it has been modified
              [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
            }
          });
        }

        // set featured media as active in the media gallery
        this.querySelector(`media-gallery`)?.setActiveMedia?.(
          `${this.dataset.section}-${variantFeaturedMediaId}`,
          true
        );

        // update media modal
        const modalContent = this.productModal?.querySelector(`.product-media-modal__content`);
        const newModalContent = html.querySelector(`product-modal .product-media-modal__content`);
        if (modalContent && newModalContent) modalContent.innerHTML = newModalContent.innerHTML;
      }

      setQuantityBoundries() {
        const data = {
          cartQuantity: this.quantityInput.dataset.cartQuantity ? parseInt(this.quantityInput.dataset.cartQuantity) : 0,
          min: this.quantityInput.dataset.min ? parseInt(this.quantityInput.dataset.min) : 1,
          max: this.quantityInput.dataset.max ? parseInt(this.quantityInput.dataset.max) : null,
          step: this.quantityInput.step ? parseInt(this.quantityInput.step) : 1,
        };

        let min = data.min;
        const max = data.max === null ? data.max : data.max - data.cartQuantity;
        if (max !== null) min = Math.min(min, max);
        if (data.cartQuantity >= data.min) min = Math.min(min, data.step);

        this.quantityInput.min = min;

        if (max) {
          this.quantityInput.max = max;
        } else {
          this.quantityInput.removeAttribute('max');
        }
        this.quantityInput.value = min;

        publish(PUB_SUB_EVENTS.quantityUpdate, undefined);
      }

      fetchQuantityRules() {
        const currentVariantId = this.productForm?.variantIdInput?.value;
        if (!currentVariantId) return;

        this.querySelector('.quantity__rules-cart .loading__spinner')?.classList.remove('hidden');
        return fetch(`${this.dataset.url}?variant=${currentVariantId}&section_id=${this.dataset.section}`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            this.updateQuantityRules(this.dataset.section, html);
          })
          .catch((e) => console.error(e))
          .finally(() => this.querySelector('.quantity__rules-cart .loading__spinner')?.classList.add('hidden'));
      }

      updateQuantityRules(sectionId, html) {
        if (!this.quantityInput) return;
        this.setQuantityBoundries();

        const quantityFormUpdated = html.getElementById(`Quantity-Form-${sectionId}`);
        const selectors = ['.quantity__input', '.quantity__rules', '.quantity__label'];
        const statusId = `quantity-status-${sectionId}`;
        const currentStatus = document.getElementById(statusId);
        const updatedStatus = html.getElementById(statusId);
        if (currentStatus && updatedStatus) {
          currentStatus.textContent = updatedStatus.textContent;
        }

        for (let selector of selectors) {
          const current = this.quantityForm.querySelector(selector);
          const updated = quantityFormUpdated.querySelector(selector);
          if (!current || !updated) continue;
          if (selector === '.quantity__input') {
            const attributes = ['data-cart-quantity', 'data-min', 'data-max', 'step', 'aria-describedby'];
            for (let attribute of attributes) {
              const valueUpdated = updated.getAttribute(attribute);
              if (valueUpdated !== null) {
                current.setAttribute(attribute, valueUpdated);
              } else {
                current.removeAttribute(attribute);
              }
            }
          } else {
            current.innerHTML = updated.innerHTML;
          }
        }
      }

      get productForm() {
        return this.querySelector(`product-form`);
      }

      get productModal() {
        return document.querySelector(`#ProductModal-${this.dataset.section}`);
      }

      get pickupAvailability() {
        return this.querySelector(`pickup-availability`);
      }

      get variantSelectors() {
        return this.querySelector('variant-selects');
      }

      get relatedProducts() {
        const relatedProductsSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'related-products'
        );
        return document.querySelector(`product-recommendations[data-section-id^="${relatedProductsSectionId}"]`);
      }

      get quickOrderList() {
        const quickOrderListSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'quick_order_list'
        );
        return document.querySelector(`quick-order-list[data-id^="${quickOrderListSectionId}"]`);
      }

      get sectionId() {
        return this.dataset.originalSection || this.dataset.section;
      }
    }
  );
}
